import React, { useState, useEffect } from 'react';
import { X, Upload, Video, Image, Save, Loader, Play, Pause } from 'lucide-react';
import FileUpload from './FileUpload';
import ContributingArtistSelector from './ContributingArtistSelector';
import PrivacySettings from './PrivacySettings';
import CopyrightSettings from './CopyrightSettings';
import { parseTagsSafely } from '../utils/parseTags';
import api from '../services/api';

// editingUpload: pass an existing upload record to edit it directly — see
// the matching comment in MusicUploadModal.jsx for the full rationale.
const VideoUploadModal = ({ isOpen, onClose, onSuccess, editingUpload = null }) => {
    const [step, setStep] = useState(1); // 1: Upload, 2: Edit Metadata
    const [uploadedFile, setUploadedFile] = useState(null);
    const [thumbnail, setThumbnail] = useState(null);
    const [metadata, setMetadata] = useState({
        title: '',
        creator: '',
        description: '',
        lyrics: '',
        tags: '',
        category: '',
        year: String(new Date().getFullYear()),
        isPublic: true,
        accessLevel: 'public',
        accessPassword: '',
        accessHint: '',
        isPaid: false,
        price: '',
        currency: 'USD',
        isCopyrighted: false,
        copyrightText: ''
    });
    const [contributingArtists, setContributingArtists] = useState([]);
    const [loading, setLoading] = useState(false);
    const [videoPreview, setVideoPreview] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [descriptionTab, setDescriptionTab] = useState('description');

    const categories = [
        'Music', 'Entertainment', 'Education', 'News', 'Comedy', 'Gaming',
        'Sports', 'Technology', 'Lifestyle', 'Travel', 'Cooking', 'Art',
        'Fashion', 'Health', 'Business', 'Science', 'Documentary', 'Other'
    ];

    // Pre-fill from the existing upload and jump straight to the metadata step.
    useEffect(() => {
        if (!isOpen || !editingUpload) return;

        setUploadedFile({
            id: editingUpload.id,
            url: editingUpload.file_url,
            originalName: editingUpload.original_filename || editingUpload.stored_filename
        });
        setThumbnail(editingUpload.cover_image_url ? { url: editingUpload.cover_image_url } : null);
        setMetadata({
            title: editingUpload.title || '',
            creator: editingUpload.creator || '',
            description: editingUpload.description || '',
            lyrics: editingUpload.lyrics || '',
            tags: parseTagsSafely(editingUpload.tags).join(', '),
            category: editingUpload.category || '',
            year: editingUpload.year ? String(editingUpload.year) : String(new Date().getFullYear()),
            isPublic: editingUpload.access_level ? editingUpload.access_level === 'public' : true,
            accessLevel: editingUpload.access_level || 'public',
            accessPassword: editingUpload.access_password || '',
            accessHint: editingUpload.access_hint || '',
            isPaid: editingUpload.access_level === 'paid' || parseFloat(editingUpload.price) > 0,
            price: editingUpload.price || '',
            currency: editingUpload.currency || 'USD',
            isCopyrighted: !!editingUpload.is_copyrighted,
            copyrightText: editingUpload.copyright_text || ''
        });
        setStep(2);

        api.get(`/contributing-artists/upload/${editingUpload.id}`)
            .then(res => {
                if (res.data.success) {
                    setContributingArtists(res.data.data.map(c => ({
                        user_id: c.artist_user_id,
                        username: c.username,
                        display_name: c.display_name,
                        profile_picture_url: c.profile_picture_url
                    })));
                }
            })
            .catch(err => console.error('Error loading existing contributing artists:', err));
    }, [isOpen, editingUpload]);

    const handleVideoUpload = (uploadData) => {
        setUploadedFile(uploadData);
        setUploadError(null);
        // Extract title from filename if no title set
        if (!metadata.title) {
            const filename = uploadData.originalName || uploadData.filename;
            const nameWithoutExt = filename.replace(/\.[^/.]+$/, "");
            setMetadata(prev => ({
                ...prev,
                title: nameWithoutExt
            }));
        }
        setStep(2);
    };

    const handleUploadError = (error) => {
        setUploadError(error);
        console.error('Upload error:', error);
    };

    const handleThumbnailUpload = (uploadData) => {
        setThumbnail(uploadData);
    };

    const handleMetadataChange = (field, value) => {
        setMetadata(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleVideoPreview = () => {
        if (!uploadedFile?.url) return;

        if (isPlaying) {
            if (videoPreview) {
                videoPreview.pause();
                setIsPlaying(false);
            }
        } else {
            const videoElement = document.getElementById('preview-video');
            if (videoElement) {
                videoElement.play();
                setIsPlaying(true);
            }
        }
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('deepwave_token');
            const response = await fetch(`/api/upload/${uploadedFile.id}/metadata`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    ...metadata,
                    tags: metadata.tags ? metadata.tags.split(',').map(tag => tag.trim()) : [],
                    isPublic: metadata.accessLevel === 'public',
                    price: metadata.isPaid ? metadata.price : ''
                })
            });

            if (response.ok) {
                // Add contributing artists if any are selected
                if (contributingArtists.length > 0) {
                    try {
                        const contributingArtistIds = contributingArtists.map(artist => artist.user_id);
                        await fetch(`/api/contributing-artists/upload/${uploadedFile.id}`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({
                                contributingArtistIds
                            })
                        });
                    } catch (contributingError) {
                        console.error('Error adding contributing artists:', contributingError);
                        // Don't fail the entire upload for this
                    }
                }
                
                onSuccess?.({
                    id: uploadedFile.id,
                    file_type: 'video',
                    title: metadata.title,
                    original_filename: uploadedFile.originalName || uploadedFile.filename,
                    cover_image_url: thumbnail?.optimizedUrl || thumbnail?.url || null
                });
                handleClose();
            } else {
                console.error('Failed to update metadata');
            }
        } catch (error) {
            console.error('Error saving metadata:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        // Cleanup
        setStep(1);
        setUploadedFile(null);
        setThumbnail(null);
        setUploadError(null);
        setMetadata({
            title: '',
            creator: '',
            description: '',
            lyrics: '',
            tags: '',
            category: '',
            year: String(new Date().getFullYear()),
            isPublic: true,
            accessLevel: 'public',
            accessPassword: '',
            accessHint: '',
            isPaid: false,
            price: '',
            currency: 'USD',
            isCopyrighted: false,
            copyrightText: ''
        });
        setContributingArtists([]);
        setIsPlaying(false);
        setDescriptionTab('description');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-gray-900 rounded-lg border border-gray-700 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-700">
                    <h2 className="text-2xl font-bold text-white">
                        {step === 1 ? 'Upload Video' : 'Edit Video Details'}
                    </h2>
                    <button
                        onClick={handleClose}
                        aria-label="Close modal"
                        className="w-8 h-8 rounded-lg bg-gray-800 hover:bg-gray-700 flex items-center justify-center transition-colors"
                    >
                        <X className="w-4 h-4 text-gray-400" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {step === 1 ? (
                        /* Step 1: File Upload */
                        <div className="space-y-6">
                            <div className="text-center">
                                <Video className="w-16 h-16 text-pink-500 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-white mb-2">
                                    Upload Your Video
                                </h3>
                                <p className="text-gray-400">
                                    Supported formats: MP4, WEBM, MOV, AVI (Max 2GB)
                                </p>
                            </div>

                            <FileUpload
                                type="video"
                                onUpload={handleVideoUpload}
                                onError={handleUploadError}
                                maxSizeMB={2048}
                                accept="video/*"
                            />

                            {/* Error Message */}
                            {uploadError && (
                                <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                                    <div className="flex items-center gap-2 text-red-400">
                                        <X className="w-4 h-4" />
                                        <span className="font-semibold">Upload Error</span>
                                    </div>
                                    <p className="text-red-300 text-sm mt-1">{uploadError}</p>
                                </div>
                            )}
                        </div>
                    ) : (
                        /* Step 2: Metadata Editing */
                        <div className="space-y-6">
                            {/* Video Preview */}
                            <div className="bg-gray-800/50 rounded-lg p-4">
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    {/* Video Player */}
                                    <div className="relative">
                                        <video
                                            id="preview-video"
                                            src={uploadedFile?.url}
                                            className="w-full max-h-96 aspect-video object-contain rounded-lg bg-gray-900"
                                            onPlay={() => setIsPlaying(true)}
                                            onPause={() => setIsPlaying(false)}
                                            onEnded={() => setIsPlaying(false)}
                                            controls
                                            controlsList="nodownload"
                                            playsInline
                                        />
                                        {!isPlaying && (
                                            <button
                                                onClick={handleVideoPreview}
                                                aria-label="Play video preview"
                                                className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg hover:bg-black/60 transition-colors"
                                            >
                                                <Play className="w-12 h-12 text-white" />
                                            </button>
                                        )}
                                    </div>

                                    {/* Video Info */}
                                    <div className="flex flex-col justify-center">
                                        <h3 className="text-white font-semibold mb-2">
                                            {uploadedFile?.originalName}
                                        </h3>
                                        <p className="text-gray-400 text-sm mb-4">
                                            Size: {(uploadedFile?.size / 1024 / 1024).toFixed(1)} MB
                                        </p>
                                        <div className="text-sm text-gray-500">
                                            Video uploaded successfully. Add details below to publish.
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Thumbnail Upload */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-300 mb-3">
                                    Custom Thumbnail
                                </label>
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-32 h-[4.5rem] bg-gray-800 rounded-lg border-2 border-dashed border-gray-600 flex items-center justify-center">
                                            {thumbnail ? (
                                                <img
                                                    src={thumbnail.url}
                                                    alt="Thumbnail"
                                                    width="128"
                                                    height="72"
                                                    className="w-full h-full object-cover rounded-lg"
                                                />
                                            ) : (
                                                <Image className="w-8 h-8 text-gray-400" />
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <FileUpload
                                            type="image"
                                            onUpload={handleThumbnailUpload}
                                            onError={(error) => console.error('Thumbnail upload error:', error)}
                                            maxSizeMB={5}
                                            accept="image/*"
                                            className="h-[4.5rem]"
                                            compact
                                            showPreview={false}
                                        >
                                            <span className="text-pink-400">Upload thumbnail</span> (optional)
                                        </FileUpload>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Recommended: 16:9 aspect ratio (e.g., 1280x720)
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Video Details */}
                            <div className="space-y-4">
                                {/* Title and Creator */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Title */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                                            Video Title *
                                        </label>
                                        <input
                                            type="text"
                                            value={metadata.title}
                                            onChange={(e) => handleMetadataChange('title', e.target.value)}
                                            className="input-field"
                                            placeholder="Enter video title"
                                            maxLength="100"
                                            required
                                        />
                                        <div className="text-xs text-gray-500 mt-1">
                                            {metadata.title.length}/100 characters
                                        </div>
                                    </div>

                                    {/* Creator */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                                            Creator/Channel
                                        </label>
                                        <input
                                            type="text"
                                            value={metadata.creator}
                                            onChange={(e) => handleMetadataChange('creator', e.target.value)}
                                            className="input-field"
                                            placeholder="Creator or channel name"
                                            maxLength="100"
                                        />
                                        <div className="text-xs text-gray-500 mt-1">
                                            {metadata.creator.length}/100 characters
                                        </div>
                                    </div>
                                </div>

                                {/* Description / Lyrics */}
                                <div>
                                    <div className="flex items-center gap-1 mb-2 border-b border-gray-700">
                                        <button
                                            type="button"
                                            onClick={() => setDescriptionTab('description')}
                                            className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 ${
                                                descriptionTab === 'description'
                                                    ? 'text-blue-400 border-blue-400'
                                                    : 'text-gray-400 border-transparent hover:text-gray-300'
                                            }`}
                                        >
                                            Description
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setDescriptionTab('lyrics')}
                                            className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 ${
                                                descriptionTab === 'lyrics'
                                                    ? 'text-blue-400 border-blue-400'
                                                    : 'text-gray-400 border-transparent hover:text-gray-300'
                                            }`}
                                        >
                                            Lyrics
                                        </button>
                                    </div>
                                    {descriptionTab === 'description' ? (
                                        <>
                                            <textarea
                                                value={metadata.description}
                                                onChange={(e) => handleMetadataChange('description', e.target.value)}
                                                className="input-field min-h-[120px] resize-vertical"
                                                placeholder="Tell viewers about your video..."
                                                maxLength="1000"
                                                rows={5}
                                            />
                                            <div className="text-xs text-gray-500 mt-1">
                                                {metadata.description.length}/1000 characters
                                            </div>
                                        </>
                                    ) : (
                                        <textarea
                                            value={metadata.lyrics}
                                            onChange={(e) => handleMetadataChange('lyrics', e.target.value)}
                                            className="input-field min-h-[120px] resize-vertical"
                                            placeholder="Add the full lyrics here..."
                                            rows={5}
                                        />
                                    )}
                                </div>

                                {/* Category, Year, and Visibility */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {/* Category */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                                            Category
                                        </label>
                                        <select
                                            value={metadata.category}
                                            onChange={(e) => handleMetadataChange('category', e.target.value)}
                                            className="input-field"
                                            style={{ color: '#fff' }}
                                        >
                                            <option value="" style={{ backgroundColor: '#1f2937', color: '#9ca3af' }}>Select category</option>
                                            {categories.map(category => (
                                                <option 
                                                    key={category} 
                                                    value={category}
                                                    style={{ backgroundColor: '#1f2937', color: '#fff' }}
                                                >
                                                    {category}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Year */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                                            Year
                                        </label>
                                        <input
                                            type="number"
                                            value={metadata.year}
                                            onChange={(e) => handleMetadataChange('year', e.target.value)}
                                            className="input-field"
                                            placeholder={String(new Date().getFullYear())}
                                            min="1900"
                                            max={new Date().getFullYear()}
                                        />
                                    </div>

                                </div>

                                {/* Access Control */}
                                <PrivacySettings
                                    value={metadata.accessLevel}
                                    password={metadata.accessPassword}
                                    passwordHint={metadata.accessHint}
                                    isPaid={metadata.isPaid}
                                    price={metadata.price}
                                    currency={metadata.currency}
                                    onChange={(v) => handleMetadataChange('accessLevel', v)}
                                    onPasswordChange={(v) => handleMetadataChange('accessPassword', v)}
                                    onHintChange={(v) => handleMetadataChange('accessHint', v)}
                                    onIsPaidChange={(v) => handleMetadataChange('isPaid', v)}
                                    onPriceChange={(v) => handleMetadataChange('price', v)}
                                    onCurrencyChange={(v) => handleMetadataChange('currency', v)}
                                    disabled={loading}
                                />

                                {/* Copyright */}
                                <CopyrightSettings
                                    isCopyrighted={metadata.isCopyrighted}
                                    copyrightText={metadata.copyrightText}
                                    onIsCopyrightedChange={(v) => handleMetadataChange('isCopyrighted', v)}
                                    onCopyrightTextChange={(v) => handleMetadataChange('copyrightText', v)}
                                    disabled={loading}
                                />

                                {/* Contributing Artists */}
                                <ContributingArtistSelector
                                    selectedArtists={contributingArtists}
                                    onArtistsChange={setContributingArtists}
                                    placeholder="Search for artists who contributed to this video..."
                                    maxArtists={10}
                                    disabled={loading}
                                />

                                {/* Tags */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                                        Tags
                                    </label>
                                    <input
                                        type="text"
                                        value={metadata.tags}
                                        onChange={(e) => handleMetadataChange('tags', e.target.value)}
                                        className="input-field"
                                        placeholder="music, cover, acoustic (separate with commas)"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Add tags to help people find your video (separate with commas)
                                    </p>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center justify-between pt-4 border-t border-gray-700">
                                <button
                                    onClick={() => setStep(1)}
                                    className="btn-secondary"
                                >
                                    Back
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={!metadata.title.trim() || loading || (metadata.isCopyrighted && !metadata.copyrightText.trim())}
                                    className="btn-primary flex items-center gap-2 disabled:opacity-50"
                                >
                                    {loading ? (
                                        <>
                                            <Loader className="w-4 h-4 animate-spin" />
                                            Publishing...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-4 h-4" />
                                            Publish Video
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VideoUploadModal;